import { computed, onMounted, ref } from "vue";

// `useRequestHeaders` is provided by Nuxt auto-imports.
import { isIPad } from "./useUnsupportedIPadOS";

export type UserAgentDeviceType = "mobile" | "tablet" | "desktop";

export interface ParsedUserAgent {
  userAgent: string;
  isIOS: boolean;
  isAndroid: boolean;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isIPad: boolean;
  deviceType: UserAgentDeviceType;
}

/**
 * Best-effort device classification from a User-Agent string only.
 * iPadOS “desktop” Safari UAs often look like Mac; combine with {@link isIPad} on the client for accuracy.
 */
export function parseUserAgent(ua: string): ParsedUserAgent {
  const isIPhoneOrIPod = /iPhone|iPod/i.test(ua);
  const isIPadClassic = /iPad/i.test(ua);
  const isAndroid = /Android/i.test(ua);
  const isAndroidMobile = isAndroid && /Mobile/i.test(ua);
  const isAndroidTablet = isAndroid && !isAndroidMobile;

  const isIOS = isIPhoneOrIPod || isIPadClassic || /like Mac OS X/i.test(ua);
  const isMobile =
    isIPhoneOrIPod ||
    isAndroidMobile ||
    /webos|blackberry|iemobile|opera mini|windows phone/i.test(ua) ||
    /\bMobi\b/i.test(ua);

  const isTablet = isIPadClassic || isAndroidTablet;
  const isDesktop = !isMobile && !isTablet;

  let deviceType: UserAgentDeviceType = "desktop";
  if (isMobile) deviceType = "mobile";
  else if (isTablet) deviceType = "tablet";

  return {
    userAgent: ua,
    isIOS,
    isAndroid,
    isMobile,
    isTablet,
    isDesktop,
    isIPad: isIPadClassic,
    deviceType,
  };
}

function mergeWithIPadClient(
  parsed: ParsedUserAgent,
  ipadClient: boolean,
): ParsedUserAgent {
  if (!ipadClient) return parsed;

  return {
    ...parsed,
    isIOS: true,
    isIPad: true,
    isTablet: true,
    isMobile: false,
    isDesktop: false,
    deviceType: "tablet",
  };
}

/**
 * Exposes the current User-Agent and coarse device flags.
 * Uses the request header during SSR; on the client reads `navigator.userAgent` and refines iPad detection.
 */
export function useUserAgent() {
  const headers = useRequestHeaders(["user-agent"]);
  const headerUa = headers["user-agent"];
  const initialUa =
    (typeof headerUa === "string" && headerUa) ||
    (import.meta.client && typeof navigator !== "undefined"
      ? (navigator.userAgent ?? "")
      : "");

  const userAgent = ref(initialUa);
  const isIPadClient = ref(false);

  onMounted(() => {
    if (!import.meta.client) return;
    userAgent.value = navigator.userAgent ?? "";
    isIPadClient.value = isIPad();
  });

  const parsed = computed(() =>
    mergeWithIPadClient(parseUserAgent(userAgent.value), isIPadClient.value),
  );

  return {
    userAgent,
    parsed,
  };
}
